import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Plus, Search, Wallet, TrendingUp, AlertCircle, Calendar, FileSpreadsheet, Ban } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/format';
import { PAYMENT_METHODS } from '../lib/constants';
import { matchesCollection } from '../lib/collectionLedger';
import CollectionModal from '../components/CollectionModal';
import HistoricalImportModal from '../components/HistoricalImportModal';
import PaymentCancellationModal from '../components/PaymentCancellationModal';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from '../contexts/AuthContext';

export default function CollectionsPage() {
    const isMobile = useIsMobile();
    const { user } = useAuth();
    const canCancel = user?.role === 'admin' || user?.role === 'sales';
    const [searchParams, setSearchParams] = useSearchParams();

    const [collections, setCollections] = useState([]);
    const [kpis, setKpis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [cancellationModalOpen, setCancellationModalOpen] = useState(false);

    const searchQuery = searchParams.get("q") || "";
    const methodFilter = searchParams.get("method") || "all";

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [colRes, kpiRes] = await Promise.all([
                api.get('/collections/ledger'),
                api.get('/dashboard/kpis')
            ]);
            setCollections(colRes.data);
            setKpis(kpiRes.data);
        } catch (error) {
            setError("Tahsilatlar yüklenemedi. Lütfen tekrar deneyin.");
        } finally {
            setLoading(false);
        }
    };

    const updateParam = (key, value) => {
        if (value && value !== 'all') {
            searchParams.set(key, value);
        } else {
            searchParams.delete(key);
        }
        setSearchParams(searchParams);
    };

    const filtered = collections.filter(c => matchesCollection(c, searchQuery, methodFilter));

    return (
        <div className="space-y-6 pb-20 md:pb-0">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h1 className="text-2xl font-bold text-gp-graphite tracking-tight">Tahsilatlar</h1>
                <div className="flex items-center gap-2">
                    {user?.role === 'admin' && (
                        <Button
                            variant="outline"
                            onClick={() => setImportModalOpen(true)}
                            className="border-gray-300 text-gp-graphite hover:bg-gray-50"
                        >
                            <FileSpreadsheet className="w-4 h-4 mr-2 text-gp-blue" />
                            Geçmiş Tahsilatları İçe Aktar
                        </Button>
                    )}
                    {canCancel && (
                        <Button
                            variant="destructive"
                            onClick={() => setCancellationModalOpen(true)}
                            data-testid="open-payment-cancellation-modal-btn"
                        >
                            <Ban className="w-4 h-4 mr-1.5" />
                            Tahsilat İptali
                        </Button>
                    )}
                    {!isMobile && (
                        <Button onClick={() => setModalOpen(true)} className="bg-brand-red hover:bg-brand-red-hover text-white">
                            <Plus className="w-4 h-4 mr-2" />
                            Tahsilat Ekle
                        </Button>
                    )}
                </div>
            </div>

            {kpis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="bg-green-100 p-2 rounded-lg"><Wallet className="w-5 h-5 text-green-600" /></div>
                            <div>
                                <p className="text-sm text-gray-500">Bu Ay Tahsilat</p>
                                <p className="font-bold">{formatCurrency(kpis.this_month_collection)}</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="bg-blue-100 p-2 rounded-lg"><TrendingUp className="w-5 h-5 text-gp-blue" /></div>
                            <div>
                                <p className="text-sm text-gray-500">Tahsilat Oranı</p>
                                <p className="font-bold">%{parseFloat(kpis.collection_percentage || 0).toFixed(1)}</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="bg-orange-100 p-2 rounded-lg"><Calendar className="w-5 h-5 text-orange-600" /></div>
                            <div>
                                <p className="text-sm text-gray-500">Açık Alacak</p>
                                <p className="font-bold">{formatCurrency(kpis.open_receivable)}</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="bg-red-100 p-2 rounded-lg"><AlertCircle className="w-5 h-5 text-gp-red" /></div>
                            <div>
                                <p className="text-sm text-gray-500">Geciken Alacak</p>
                                <p className="font-bold">{formatCurrency(kpis.overdue_receivable)}</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <div className="flex flex-col md:flex-row gap-4 bg-white p-4 border rounded-lg shadow-sm">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                        placeholder="Müşteri veya İş No ile ara..."
                        className="pl-9"
                        value={searchQuery}
                        onChange={e => updateParam('q', e.target.value)}
                    />
                </div>
                <div className="w-full md:w-64">
                    <Select value={methodFilter} onValueChange={v => updateParam('method', v)}>
                        <SelectTrigger>
                            <SelectValue placeholder="Tahsilat Yöntemi" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tüm Yöntemler</SelectItem>
                            {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {error ? (
                <div role="alert" className="p-4 border rounded-lg text-red-700">
                    {error} <Button variant="outline" onClick={fetchData}>Tekrar Dene</Button>
                </div>
            ) : loading ? (
                <p role="status">Tahsilatlar yükleniyor...</p>
            ) : (
                <Card>
                    <div className="hidden md:block">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Tarih</TableHead>
                                    <TableHead>Müşteri</TableHead>
                                    <TableHead>İş No</TableHead>
                                    <TableHead>Yöntem</TableHead>
                                    <TableHead className="text-right">Tutar</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                            Tahsilat bulunamadı.
                                        </TableCell>
                                    </TableRow>
                                ) : filtered.map(c => (
                                    <TableRow key={c.id}>
                                        <TableCell>{formatDate(c.collection_date)}</TableCell>
                                        <TableCell className="font-medium">{c.customer_name || 'Bilinmiyor'}</TableCell>
                                        <TableCell>{c.job_number || 'Müşteri avansı'}</TableCell>
                                        <TableCell>{PAYMENT_METHODS[c.payment_method] || c.payment_method}</TableCell>
                                        <TableCell className="text-right font-semibold">
                                            {formatCurrency(c.amount)}
                                            {c.status === 'cancelled' && <span className="block text-xs text-red-600">İptal Edildi</span>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="block md:hidden">
                        {filtered.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">Tahsilat bulunamadı.</div>
                        ) : filtered.map(c => (
                            <div key={c.id} className="p-4 border-b last:border-0 flex justify-between items-center">
                                <div>
                                    <p className="font-bold text-gp-graphite">{c.customer_name || 'Bilinmiyor'}</p>
                                    <p className="text-sm text-gray-500">{formatDate(c.collection_date)} • {PAYMENT_METHODS[c.payment_method]}</p>
                                </div>
                                <div className="font-bold text-gp-blue text-right">
                                    {formatCurrency(c.amount)}
                                    {c.status === 'cancelled' && <p className="text-xs text-red-600">İptal Edildi</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {cancellationModalOpen && (
                <PaymentCancellationModal
                    open={cancellationModalOpen}
                    onOpenChange={setCancellationModalOpen}
                    onSuccess={fetchData}
                    collections={collections}
                />
            )}

            {modalOpen && (
                <CollectionModal
                    open={modalOpen}
                    onOpenChange={setModalOpen}
                    onSuccess={fetchData}
                />
            )}

            {importModalOpen && (
                <HistoricalImportModal
                    open={importModalOpen}
                    onOpenChange={setImportModalOpen}
                    onSuccess={fetchData}
                />
            )}
        </div>
    );
}
